import React from 'react';
import AsyncSelect from 'react-select/async';
import { QueryObserverResult } from 'react-query';
import NoSsr from '@common/noSsr';
import { useToast } from '@common/toast';
import Modal from '@components/common/modal';
import Icon from '@components/common/assets';
import Button from '@components/common/button';
import { ReactSelectInputStyles } from '@components/match/profile/matchProposal/blindCompanyListModal/reactSelectStyles';
import ChipList from '@components/match/profile/matchProposal/blindCompanyListModal/chipList';
import { useSaveBlindCompanies } from '@repository/profiles/useProfile';
import { useCompanies } from '@repository/companies/useCompanies';
import BlindCompanyListRs, { BlindCompanyDto } from '@domain/rs/profile/blind/BlindCompanyListRs';
import { LoadOptions } from '@domain/constant/ReactSelectOption';
import Colors from '@domain/constant/colors';
import useDebounce from '@utils/hooks/useDebounce';

import classnames from 'classnames/bind';
import styles from './blindCompanyListModal.module.scss';
const cx = classnames.bind(styles);

interface IBlindCompanyListModalProps {
  visible: boolean;
  onClose: () => void;
  blindCompanies: BlindCompanyDto[];
  refetch: () => Promise<QueryObserverResult<BlindCompanyListRs, unknown>>;
}

const BlindCompanyListModal = ({ onClose, visible, blindCompanies, refetch }: IBlindCompanyListModalProps) => {
  const Toast = useToast();

  const { mutate: saveBlindCompaniesMutate } = useSaveBlindCompanies(refetch, (err) => {
    switch (err?.response?.data?.errorCode) {
      case 'B901':
        Toast({ type: 'error', iconType: 'info', content: '이미 등록된 기업입니다.' });
        return;
      default:
        Toast({ type: 'error', iconType: 'info', content: err.response?.data.message });
        return;
    }
  });

  const { mutateAsync: mutateAsyncCompany } = useCompanies();
  const loadOptions = useDebounce((keyword: string, callback: LoadOptions) => {
    mutateAsyncCompany({ keyword: keyword, size: 10000, page: 0 }).then((schools) => {
      callback(schools);
    });
  }, 500);

  const handleDeleteBlindCompany = (deleteSn: number) => {
    if (!blindCompanies) return;

    saveBlindCompaniesMutate(blindCompanies.map((item) => item.sn).filter((sn) => sn !== deleteSn));
  };

  const handleAddBlindCompany = (selected: any) => {
    if (!selected) return;

    const companies = blindCompanies.map((item) => item.sn) ?? [];
    const selectedSn = selected.value.sn;

    //이미 선택된 기업이 있는 경우
    if (companies.some((item) => item === selectedSn)) {
      Toast({ type: 'error', iconType: 'info', content: '이미 등록된 기업입니다.' });
      return;
    }

    saveBlindCompaniesMutate([...companies, selected.value.sn]);
  };

  return (
    <Modal visible={visible} dimmed={true}>
      <div className={cx('blindCompaniesModal')}>
        <div className={cx('blindArea')}>
          <div className={cx('titleArea')}>
            <div className={cx('title')}>프로필 열람 제한 기업</div>
            <div className={cx('description')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
              <p>
                열람 제한으로 등록한 기업에서는 내 프로필을 확인할 수 없습니다.
                <br />
                단, 입사지원한 경우에는 내 정보가 보여지게 됩니다.
              </p>
            </div>
          </div>
          <div className={cx('selectArea')}>
            <NoSsr>
              <AsyncSelect
                className={cx('select')}
                loadOptions={loadOptions as any}
                placeholder={'기업명을 검색해주세요.'}
                openMenuOnClick={false}
                styles={ReactSelectInputStyles}
                value={null}
                isClearable={true}
                onChange={handleAddBlindCompany}
              />
            </NoSsr>
          </div>

          <ChipList blindCompanies={blindCompanies} onDeleteBlindCompany={handleDeleteBlindCompany} />
        </div>

        <div className={cx('confirmButton')}>
          <Button className={cx('button')} onClick={onClose}>
            확인
          </Button>
        </div>

        <a className={cx('btnClose')} onClick={onClose} role="button">
          <span className={cx('hidden')}>close</span>
        </a>
      </div>
    </Modal>
  );
};

export default BlindCompanyListModal;
